import type { ReactNode } from 'react';
import React, { useCallback } from 'react';
import { Trans } from '@lingui/macro';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';

import { isDevEnv } from '@rover/rsdk/src/modules/Env/node';

type Props = {
  children?: ReactNode | null | undefined;
  message?: string;
  location?: string;
  topLevel?: boolean;
};

const ErrorBoundary = ({ children, message, topLevel, location }: Props): JSX.Element => {
  const renderFallback = useCallback(
    ({ error, componentStack }) => {
      if (topLevel && isDevEnv()) {
        return (
          // Inline style usage because the Kibble ThemeProvider won't be available
          <div style={{ backgroundColor: '#0d0208', height: '100vh', overflow: 'auto' }}>
            <pre style={{ whiteSpace: 'pre-wrap', color: '#00ff41', margin: 0, padding: 16 }}>
              {error.toString()}
              {componentStack}
            </pre>
          </div>
        );
      }
      return message ? (
        <h4>{message}</h4>
      ) : (
        <h4>
          <Trans>Something went wrong.</Trans>
        </h4>
      );
    },
    [message, topLevel]
  );
  const beforeCapture = useCallback(
    (scope) => {
      scope.setTag('ErrorBoundary', topLevel ? 'topLevel' : location);
    },
    [topLevel, location]
  );

  return (
    <SentryErrorBoundary beforeCapture={beforeCapture} fallback={renderFallback}>
      {children || null}
    </SentryErrorBoundary>
  );
};

export default ErrorBoundary;
